import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Loader2, Calendar as CalendarIcon, ChevronRight, Trash2, History, Info, Check, ChevronsUpDown, Search, AlertTriangle, CalendarDays } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../components/ui/utils";
import { Textarea } from "../../components/ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Calendar, dateFnsLocalizer, Event as CalendarEvent } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, addMonths, subMonths, addWeeks, subWeeks, setMonth, setYear, startOfMonth, endOfMonth, endOfWeek } from "date-fns";
import { enUS } from "date-fns/locale";
import { api } from "../../utils/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { useData } from "../../context/DataContext";
import { Trash } from "lucide-react";

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Custom types
type LeaveType = "Annual" | "Personal" | string;

interface LeaveEvent extends CalendarEvent {
  id: string;
  title: string;
  type: LeaveType;
  statusLabel: string;
  start: Date;
  end: Date;
  hours?: string;
  allDay?: boolean;
  deleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
  deletionReason?: string;
}

interface UpcomingLeave {
  id: string;
  name: string;
  initials: string;
  department: string;
  startDate: string;
  endDate: string;
  appliedAt: string;
  type: string;
  status: string;
  statusLabel: string;
  conflictCount: number;
}

const LEAVE_COLORS: Record<string, { bg: string; text: string }> = {
  "Annual Leave": { bg: "#16a34a", text: "#ffffff" }, // Green
  "Personal Leave": { bg: "#3b82f6", text: "#ffffff" }, // Blue
  "Unpaid Leave": { bg: "#f97316", text: "#ffffff" }, // Orange
  "Holiday": { bg: "#f97316", text: "#ffffff" },      // Orange (Public Holiday)
  "Other": { bg: "#6b7280", text: "#ffffff" },    // Gray
};



const LeaveCalendar = () => {
  const navigate = useNavigate();
  const { departments } = useData();
  const [department, setDepartment] = useState("all");
  const [departmentSearchOpen, setDepartmentSearchOpen] = useState(false);
  const [leaveType, setLeaveType] = useState("all");
  const [showMyTeam, setShowMyTeam] = useState(false);
  const [showAllDepartments, setShowAllDepartments] = useState(true);

  const [events, setEvents] = useState<LeaveEvent[]>([]);
  const [upcomingLeaves, setUpcomingLeaves] = useState<UpcomingLeave[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<any>('month');

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Soft delete states
  const { currentUser } = useAuth();
  const [isDeleteReasonModalOpen, setIsDeleteReasonModalOpen] = useState(false);
  const [deletionReason, setDeletionReason] = useState("");
  const [eventToSoftDelete, setEventToSoftDelete] = useState<LeaveEvent | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDeleteAbsence = currentUser && ['Admin', 'Director', 'Senior Manager', 'Manager'].includes(currentUser.userRole);

  useEffect(() => {
    fetchCalendarData();
    fetchUpcomingLeaves();
  }, [department, leaveType, showMyTeam]);

  const handleSelectEvent = (event: LeaveEvent) => {
    setSelectedDate(event.start);
    setIsModalOpen(true);
  };

  const getLeavesForSelectedDate = () => {
    if (!selectedDate) return [];

    // Normalize selected date to YYYY-MM-DD
    const dateStr = format(selectedDate, "yyyy-MM-dd");

    return events.filter(event => {
      const eventStartStr = format(event.start, "yyyy-MM-dd");
      const eventEndStr = format(event.end, "yyyy-MM-dd");
      return dateStr >= eventStartStr && dateStr <= eventEndStr;
    }).sort((a, b) => {
      // Sort active leaves (deleted: false/undefined) before deleted leaves (deleted: true)
      if (a.deleted === b.deleted) return 0;
      return a.deleted ? 1 : -1;
    });
  };

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (department !== 'all') params.append('department', department);
      if (leaveType !== 'all') params.append('leaveType', leaveType);
      if (showMyTeam) params.append('showMyTeam', 'true');

      const res = await api.get(`/api/attendance/team-leaves-calendar?${params.toString()}`);
      if (res.success) {
        const transformedEvents = res.data.map((event: any) => ({
          ...event,
          start: new Date(event.start),
          end: new Date(event.end),
        })).sort((a: any, b: any) => {
          if (!!a.deleted === !!b.deleted) return 0;
          return a.deleted ? 1 : -1;
        });
        setEvents(transformedEvents);
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingLeaves = async () => {
    try {
      const res = await api.get('/api/attendance/upcoming-leaves');
      if (res.success) {
        setUpcomingLeaves(res.data);
      }
    } catch (error) {
      console.error("Error fetching upcoming leaves:", error);
    }
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const leaveEvent = event as LeaveEvent;
    const colors = LEAVE_COLORS[leaveEvent.type] || LEAVE_COLORS["Annual Leave"];

    let borderColor = colors.bg;
    if (leaveEvent.type === 'Annual Leave') borderColor = '#15803d';
    if (leaveEvent.type === 'Personal Leave') borderColor = '#2563eb';
    if (leaveEvent.type === 'Unpaid Leave') borderColor = '#c2410c';

    const isPending = leaveEvent.statusLabel === 'Pending Request';
    const isHoliday = (leaveEvent as any).isHoliday;

    const style = {
      backgroundColor: isPending ? '#ffffff' : colors.bg,
      borderRadius: '10px',
      opacity: leaveEvent.deleted ? (isHoliday ? 0.7 : 0.4) : (isHoliday ? 1 : 0.85),
      color: isPending ? borderColor : colors.text,
      border: isHoliday ? `2px solid #ea580c` : `2px solid ${borderColor}`,
      display: 'block',
      fontSize: '11px',
      fontWeight: '700',
      padding: '4px 8px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      marginTop: '2px',
      textDecoration: leaveEvent.deleted ? 'line-through' : 'none',
      filter: leaveEvent.deleted ? 'grayscale(0.5)' : 'none'
    };

    return {
      style,
      className: cn(
        leaveEvent.deleted ? 'event-deleted' : '',
        isPending ? 'event-pending' : '',
        isHoliday ? 'event-holiday' : ''
      )
    };
  };

  const CustomToolbar = (toolbar: any) => {
    const goToBack = () => {
      toolbar.onNavigate('PREV');
      setCurrentDate(
        view === 'month' ? subMonths(currentDate, 1) : subWeeks(currentDate, 1)
      );
    };

    const goToNext = () => {
      toolbar.onNavigate('NEXT');
      setCurrentDate(
        view === 'month' ? addMonths(currentDate, 1) : addWeeks(currentDate, 1)
      );
    };

    const goToCurrent = () => {
      toolbar.onNavigate('TODAY');
      setCurrentDate(new Date());
    };

    return (
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8 bg-white p-4 rounded-[24px] border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="p-2.5 bg-blue-50 rounded-xl">
            <CalendarIcon className="text-blue-500" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-black text-gray-900 leading-tight">
              {format(toolbar.date, 'MMMM yyyy')}
            </h2>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              {view} view • {(() => {
                const start = view === 'agenda' || view === 'month' ? startOfMonth(toolbar.date) : startOfWeek(toolbar.date);
                const end = view === 'agenda' || view === 'month' ? endOfMonth(toolbar.date) : endOfWeek(toolbar.date);
                return events.filter(e => e.start >= start && e.start <= end).length;
              })()} Events
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select
            value={toolbar.date.getMonth().toString()}
            onValueChange={(val) => {
              const newDate = setMonth(toolbar.date, parseInt(val));
              toolbar.onNavigate('DATE', newDate);
              setCurrentDate(newDate);
            }}
          >
            <SelectTrigger className="w-[130px] rounded-xl h-10 border-gray-100 bg-gray-50/50 font-bold text-xs">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-100">
              {Array.from({ length: 12 }, (_, i) => {
                const monthName = format(setMonth(new Date(), i), 'MMMM');
                return (
                  <SelectItem key={monthName} value={i.toString()} className="text-xs font-bold py-2">
                    {monthName}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>

          <Select
            value={toolbar.date.getFullYear().toString()}
            onValueChange={(val) => {
              const newDate = setYear(toolbar.date, parseInt(val));
              toolbar.onNavigate('DATE', newDate);
              setCurrentDate(newDate);
            }}
          >
            <SelectTrigger className="w-[100px] rounded-xl h-10 border-gray-100 bg-gray-50/50 font-bold text-xs">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-100 max-h-[300px]">
              {Array.from({ length: 16 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                <SelectItem key={year} value={year.toString()} className="text-xs font-bold py-2">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1.5 p-1 bg-gray-50 rounded-xl border border-gray-100">
          <Button
            variant="ghost"
            size="sm"
            className={cn("rounded-lg h-8 text-xs font-bold transition-all", view === 'month' ? "bg-white shadow-sm text-slate-900" : "text-gray-500")}
            onClick={() => { setView('month'); toolbar.onView('month'); }}
          >
            Month
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn("rounded-lg h-8 text-xs font-bold transition-all", view === 'week' ? "bg-white shadow-sm text-slate-900" : "text-gray-500")}
            onClick={() => { setView('week'); toolbar.onView('week'); }}
          >
            Week
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn("rounded-lg h-8 text-xs font-bold transition-all", view === 'agenda' ? "bg-white shadow-sm text-slate-900" : "text-gray-500")}
            onClick={() => { setView('agenda'); toolbar.onView('agenda'); }}
          >
            Agenda
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={goToCurrent}
            className="rounded-xl h-10 border-gray-100 font-bold text-xs px-4"
          >
            Today
          </Button>
          <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-xl border border-gray-100">
            <Button
              variant="ghost"
              size="icon"
              onClick={goToBack}
              className="h-9 w-9 rounded-lg text-gray-600 hover:bg-white hover:shadow-sm"
            >
              <ChevronLeft size={18} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              className="h-9 w-9 rounded-lg text-gray-600 hover:bg-white hover:shadow-sm"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const formatDateRange = (startStr: string, endStr: string) => {
    const start = new Date(startStr);
    const end = new Date(endStr);

    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    if (start.getTime() === end.getTime()) {
      return start.toLocaleDateString('en-GB', options);
    }
    return `${start.toLocaleDateString('en-GB', options)} - ${end.toLocaleDateString('en-GB', options)}`;
  };

  const handleSoftDelete = async () => {
    if (!eventToSoftDelete || !deletionReason.trim()) {
      toast.error("Please provide a reason for deletion");
      return;
    }

    setIsDeleting(true);
    try {
      // Extract the original ID if it was prefixed with 'absence-'
      const originalId = eventToSoftDelete.id.replace('absence-', '');

      const response = await api.patch(`/api/attendance/absence/${originalId}/soft-delete`, {
        deletionReason
      });

      if (response.success) {
        toast.success("Absence record updated (Soft Deleted)");
        setIsDeleteReasonModalOpen(false);
        setDeletionReason("");
        setEventToSoftDelete(null);
        fetchCalendarData(); // Refresh calendar to reflect the deleted status
        fetchUpcomingLeaves(); // Refresh upcoming list to remove the deleted record
      } else {
        toast.error(response.message || "Failed to delete record");
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      toast.error(error.message || "An error occurred during deletion");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="outline"
          size="icon"
          className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
          onClick={() => navigate("/attendance")}
        >
          <ChevronLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Leave Calendar</h1>
          <p className="text-gray-500 font-medium text-sm">Company-wide leave schedule and availability</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                Filters {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              </h3>
              <p className="text-gray-500 text-sm font-medium">Customize calendar view</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Department</label>
              <Popover open={departmentSearchOpen} onOpenChange={setDepartmentSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={departmentSearchOpen}
                    className="w-full justify-between rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all text-gray-900 font-normal hover:bg-gray-50"
                  >
                    {department === "all" ? (
                      "All Departments"
                    ) : (
                      departments.find((d) => d.departmentName === department)?.departmentName || department
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl border-gray-100 shadow-lg">
                  <Command className="rounded-xl">
                    <CommandInput
                      placeholder="Search department..."
                      className="!border-none !ring-0 !ring-offset-0 focus:!ring-0 focus:!border-none focus-visible:!ring-0 shadow-none outline-none focus:outline-none"
                    />
                    <CommandEmpty>No department found.</CommandEmpty>
                    <CommandGroup className="max-h-[300px] overflow-auto">
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setDepartment("all");
                          setDepartmentSearchOpen(false);
                        }}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            department === "all" ? "opacity-100" : "opacity-0"
                          )}
                        />
                        All Departments
                      </CommandItem>
                      {departments.map((dept) => (
                        <CommandItem
                          key={dept.id}
                          value={dept.departmentName}
                          onSelect={() => {
                            setDepartment(dept.departmentName);
                            setDepartmentSearchOpen(false);
                          }}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              department === dept.departmentName ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {dept.departmentName}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-900 tracking-wide ml-1">Leave Type</label>
              <Select value={leaveType} onValueChange={setLeaveType}>
                <SelectTrigger className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white transition-all">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-gray-100">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Annual Leave">Annual Leave</SelectItem>
                  <SelectItem value="Personal Leave">Personal Leave</SelectItem>
                  <SelectItem value="Unpaid Leave">Unpaid Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 flex flex-col justify-end">
              <label className="text-xs font-bold text-gray-900 tracking-wide ml-1 mb-2">Legend</label>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-green-600">Annual</span>
                <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-blue-500">Personal</span>
                <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-orange-500">Unpaid</span>
                <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-orange-400">Holiday</span>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Wrapper */}
        <div className="bg-white rounded-[32px] p-6 border border-gray-100 shadow-sm overflow-hidden h-[800px]">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">Loading Calendar...</p>
              </div>
            </div>
          ) : (
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              views={["month", "week", "agenda"]}
              view={view}
              onView={(newView) => setView(newView)}
              date={currentDate}
              onNavigate={(date) => setCurrentDate(date)}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleSelectEvent}
              components={{
                toolbar: CustomToolbar,
              }}
              popup
            />
          )}
          <style>{`
                .rbc-calendar {
                    font-family: inherit;
                }
                .rbc-month-view {
                    border-radius: 20px;
                    border: 1px solid #f3f4f6;
                    overflow: hidden;
                    background: #fff;
                }
                .rbc-header {
                    padding: 12px 10px !important;
                    font-size: 11px !important;
                    font-weight: 800 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                    color: #9ca3af !important;
                    border-bottom: 1px solid #f3f4f6 !important;
                    background: #f9fafb !important;
                }
                .rbc-month-row {
                    border-bottom: 1px solid #f3f4f6 !important;
                }
                .rbc-day-bg + .rbc-day-bg {
                    border-left: 1px solid #f3f4f6 !important;
                }
                .rbc-today {
                    background-color: #f8fafc !important;
                }
                .rbc-off-range-bg {
                    background-color: #fafafa !important;
                }
                .rbc-event {
                    transition: all 0.2s ease;
                }
                .rbc-event:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                }
                .rbc-show-more {
                    font-size: 10px !important;
                    font-weight: 800 !important;
                    text-transform: uppercase !important;
                    color: #3b82f6 !important;
                    background: transparent !important;
                }
                .rbc-agenda-view table {
                    border: 1px solid #f3f4f6 !important;
                    border-radius: 20px !important;
                    overflow: hidden !important;
                }
                .rbc-agenda-view table thead tr th {
                    background: #f9fafb !important;
                    padding: 15px !important;
                    font-size: 11px !important;
                    font-weight: 800 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                    color: #9ca3af !important;
                    text-align: left !important;
                }
                .rbc-agenda-date-cell, .rbc-agenda-time-cell, .rbc-agenda-event-cell {
                    padding: 15px !important;
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    color: #374151 !important;
                    border-bottom: 1px solid #f3f4f6 !important;
                }
                .event-deleted .rbc-event-content {
                    text-decoration: line-through !important;
                }
            `}</style>
        </div>

        {/* Modal for Selected Date */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="sm:max-w-[500px] rounded-[24px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                Leaves for {selectedDate ? format(selectedDate, "EEE, dd MMM yyyy") : ""}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {getLeavesForSelectedDate().length === 0 ? (
                <div className="text-center py-10 text-gray-500  font-medium bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                  No one is on leave on this day.
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  {getLeavesForSelectedDate().map((event) => (
                    <div
                      key={event.id}
                      className={cn(
                        "flex flex-col p-4 rounded-2xl border border-gray-100 bg-white shadow-sm transition-all relative overflow-hidden",
                        event.deleted && "opacity-60 bg-gray-50/50"
                      )}
                    >
                      {event.deleted && (
                        <div className="absolute top-0 right-0 py-1 px-3 bg-gray-200 text-gray-500 text-[10px] font-black uppercase tracking-widest rounded-bl-xl border-l border-b border-gray-300 shadow-sm z-10 flex items-center gap-1">
                          <Trash size={10} /> Deleted
                        </div>
                      )}

                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Avatar className={cn("h-10 w-10 text-sm font-medium border border-gray-100 shadow-sm", event.deleted && "grayscale")}>
                            <AvatarFallback className={cn("font-bold uppercase", (event as any).isHoliday ? "bg-orange-50 text-orange-600" : "bg-gray-50 text-gray-600")}>
                              {(event as any).isHoliday ? <CalendarDays className="h-5 w-5" /> : (event.title ? event.title.split(' ').map(n => n[0]).join('').substring(0, 2) : "??")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className={cn("text-sm font-bold text-gray-900", event.deleted && "line-through text-gray-400")}>
                              {event.title}
                            </h4>
                            <p className="text-xs text-gray-400 font-medium">
                              {event.statusLabel}
                              {event.hours && parseFloat(event.hours) < 8 && ` • ${event.hours} hrs`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${event.type === 'Annual Leave' ? 'bg-green-600' :
                              event.type === 'Unpaid Leave' ? 'bg-orange-500' :
                                (event as any).isHoliday ? 'bg-orange-400' :
                                  'bg-blue-500'
                            } hover:opacity-90 text-white shadow-none px-3 py-0.5 rounded-full font-bold h-fit`}>
                            {event.type}
                          </Badge>
                          {canDeleteAbsence && !event.deleted && event.id.startsWith('absence-') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-400 hover:text-red-500 hover:bg-red-50 rounded-lg translate-x-1"
                              onClick={() => {
                                setEventToSoftDelete(event);
                                setIsDeleteReasonModalOpen(true);
                              }}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </div>

                      {event.deleted && (
                        <div className="mt-2 pt-3 border-t border-gray-100 space-y-2">
                          <div className="flex items-start gap-2 text-[11px] text-gray-500 bg-white/60 p-2 rounded-xl border border-gray-100/50">
                            <Info className="shrink-0 mt-0.5 text-blue-400" size={14} />
                            <div>
                              <p className="font-bold text-gray-700">Audit Information</p>
                              <p className="mt-0.5 ">Deleted by <span className="font-bold text-gray-900">{event.deletedBy}</span> on {event.deletedAt ? format(new Date(event.deletedAt), 'dd/MM/yyyy HH:mm') : 'N/A'}</p>
                              <p className="mt-1 text-gray-600 leading-relaxed font-medium">Reason: {event.deletionReason || "No reason provided"}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Soft Delete Reason Dialog */}
        <Dialog open={isDeleteReasonModalOpen} onOpenChange={setIsDeleteReasonModalOpen}>
          <DialogContent className="sm:max-w-[450px] rounded-[32px] p-0 overflow-hidden border-none shadow-2xl">
            <div className="bg-red-50 p-8 flex flex-col items-center text-center gap-4">
              <div className="h-16 w-16 bg-white rounded-2xl flex items-center justify-center text-red-500 shadow-sm border border-red-100">
                <Trash2 size={32} />
              </div>
              <div>
                <DialogTitle className="text-2xl font-black text-gray-900 leading-tight mb-1">Confirm Deletion</DialogTitle>
                <DialogDescription className="text-gray-500 font-medium">
                  This record will be struck out on the calendar as a mistake. Please provide a reason for this audit trail.
                </DialogDescription>
              </div>
            </div>

            <div className="p-8 bg-white space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest ml-1 flex items-center gap-2">
                  Deletion Reason <span className="text-red-500 text-lg">*</span>
                </label>
                <Textarea
                  placeholder="e.g. Employee actually showed up late, or wrong date selected..."
                  className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[120px] focus:bg-white focus:ring-1 focus:ring-red-100 transition-all font-medium text-gray-900 resize-none text-sm"
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl h-12 font-bold text-xs uppercase tracking-widest border-gray-100 text-gray-500"
                  onClick={() => setIsDeleteReasonModalOpen(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 rounded-xl h-12 font-bold text-xs uppercase tracking-widest bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-100"
                  onClick={handleSoftDelete}
                  disabled={isDeleting || !deletionReason.trim()}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Confirm Deletion"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upcoming Leave */}
        <div className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm space-y-6">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Upcoming Leave</h3>
            <p className="text-gray-500 text-sm font-medium">Leave scheduled for the next 90 days</p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-100">
            <Table>
              <TableHeader className="bg-gray-50/50 text-[10px] uppercase tracking-widest font-black">
                <TableRow>
                  <TableHead className="py-4 px-6 font-black text-gray-400">Request Date</TableHead>
                  <TableHead className="py-4 px-6 font-black text-gray-400">Name</TableHead>
                  <TableHead className="py-4 px-6 font-black text-gray-400">Date Range</TableHead>
                  <TableHead className="py-4 px-6 font-black text-gray-400">Status</TableHead>
                  <TableHead className="py-4 px-6 font-black text-gray-400 text-center">Team Coverage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingLeaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-20 text-gray-500 font-medium bg-gray-50/50">
                      No upcoming leaves scheduled.
                    </TableCell>
                  </TableRow>
                ) : (
                  upcomingLeaves.map((leave) => (
                    <TableRow
                      key={leave.id}
                      className="group cursor-pointer hover:bg-gray-50/80 transition-all border-gray-50"
                      onClick={() => {
                        const tab = leave.status.toLowerCase() === 'approved' ? 'approved' : 'pending';
                        navigate(`/attendance/approvals?tab=${tab}`);
                      }}
                    >
                      <TableCell className="py-4 px-6 text-xs text-gray-500 font-bold whitespace-nowrap">
                        {new Date(leave.appliedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8 text-[10px] font-bold border border-gray-100 shadow-sm">
                            <AvatarFallback className="bg-gray-50 text-gray-600 uppercase">
                              {leave.initials}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate max-w-[150px]">
                            {leave.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-6 text-sm font-bold text-gray-700 whitespace-nowrap">
                        {formatDateRange(leave.startDate, leave.endDate)}
                      </TableCell>
                      <TableCell className="py-4 px-6">
                        <Badge
                          className={cn(
                            "shadow-none px-3 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border-0",
                            leave.status.toLowerCase() === 'approved' ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-blue-100 text-blue-700 hover:bg-blue-100"
                          )}
                        >
                          {leave.status.toLowerCase() === 'approved' ? 'Confirmed' : 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className={cn(
                            "px-2.5 py-1 rounded-lg text-xs font-black flex items-center gap-1.5 transition-all",
                            leave.conflictCount > 0
                              ? "bg-orange-50 text-orange-600 border border-orange-100 shadow-sm"
                              : "bg-gray-50 text-gray-400 font-bold"
                          )}>
                            {leave.conflictCount > 0 && <AlertTriangle size={12} className="shrink-0" />}
                            {leave.conflictCount} {leave.conflictCount === 1 ? 'Overlap' : 'Overlaps'}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveCalendar;
